import { useGet } from "@albertine/api-client";
import type {
    GooglePlaceBusinessStatus,
    MemberDataRatingScore,
} from "@albertine/types";
import type { CatalogItemFull } from "@albertine/types/openapi/lmt";
import { Lib } from "albertine-shared-web";
import classNames from "classnames";
import { useSuppliers } from "../../../common/hooks/useSuppliers";
import { showAddress } from "../../../common/utils/catalog.util";
import { singularPlural } from "../../../common/utils/time.formatted.util";
import Icon, { type MdIconName } from "../../Common/Icon";
import Label from "../../Common/Label";
import { ScoreTag } from "../../Common/Tag";
import { parseViewDetails } from "../CatalogItemView";
import BusinessStatus from "../GooglePlace/BusinessStatus";
import GoogleReview from "../GooglePlace/GoogleReview";
import OpeningHours from "../GooglePlace/OpeningHours";
import { useCatalogItemPlaceDetails } from "../hooks/useCatalogItemPlaceDetails";

export type PlaceDetailsPrimaryData = {
    offeringCategories: { primaryIcon: MdIconName; text: string | undefined };
    website: string | undefined;
    phoneNumber: string | undefined;
    address: string | undefined;
    addressUrl: string | undefined;
    googlePlaceUrl: string | undefined;
    showAddress: boolean;
    googleReview:
        | {
              rating: number | null | undefined;
              reviewsTotal: number | null | undefined;
              googlePlaceId: string | undefined;
          }
        | undefined;
    openingHours: google.maps.places.PlaceOpeningHours | undefined;
    businessStatus: GooglePlaceBusinessStatus | null | undefined;
    memberReviews?: MemberDataRatingScore | null;
};

export function PlaceDetailsPrimary({
    offeringCategories,
    website,
    phoneNumber,
    address,
    addressUrl,
    googlePlaceUrl,
    showAddress,
    googleReview,
    openingHours,
    businessStatus,
    memberReviews,
    className,
}: PlaceDetailsPrimaryData & { className?: string }) {
    const PrimaryCategoryIcon = Icon[offeringCategories.primaryIcon];

    return (
        <Lib.Flex.Row
            gap="value16"
            justifyContent="space-between"
            alignItems="start"
            className={classNames("catalog-item__primary-details", className)}
        >
            <Lib.Flex.Column gap="value8">
                <Lib.Flex.Row gap="value8">
                    <PrimaryCategoryIcon.Medium.N10 />

                    {offeringCategories.text ? (
                        <Label.Small.SemiBold>
                            {offeringCategories.text}
                        </Label.Small.SemiBold>
                    ) : (
                        <Label.Small.SemiBold.N60>
                            Uncategorized
                        </Label.Small.SemiBold.N60>
                    )}
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Icon.Link.Medium.N10 />

                    {website ? (
                        <Label.Small.SemiBold>
                            <a href={website} target="_blank" rel="noreferrer">
                                {website}
                            </a>
                        </Label.Small.SemiBold>
                    ) : (
                        <Label.XSmall.SemiBold.N60>Website</Label.XSmall.SemiBold.N60>
                    )}
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Icon.Call.Medium.N10 />

                    {phoneNumber ? (
                        <Label.Small.SemiBold>{phoneNumber}</Label.Small.SemiBold>
                    ) : (
                        <Label.XSmall.SemiBold.N60>Phone</Label.XSmall.SemiBold.N60>
                    )}
                </Lib.Flex.Row>

                {showAddress && (
                    <Lib.Flex.Row gap="value8">
                        <Icon.LocationOn.Medium.N10 />

                        {googlePlaceUrl && address && (
                            <a href={googlePlaceUrl} target="_blank" rel="noreferrer">
                                <Label.Small.SemiBold>{address}</Label.Small.SemiBold>
                            </a>
                        )}

                        {!googlePlaceUrl && address && addressUrl && (
                            <a href={addressUrl} target="_blank" rel="noreferrer">
                                <Label.Small.SemiBold>{address}</Label.Small.SemiBold>
                            </a>
                        )}

                        {!address && (
                            <Label.XSmall.SemiBold.N60>
                                Address
                            </Label.XSmall.SemiBold.N60>
                        )}
                    </Lib.Flex.Row>
                )}
            </Lib.Flex.Column>

            <Lib.Flex.Column gap="value8">
                {memberReviews && (
                    <Lib.Flex.Row gap="value8">
                        <Icon.StarRate.Medium.N10 />
                        {memberReviews.average !== 0 ? (
                            <>
                                <Label.Small.SemiBold>
                                    Albertine members
                                </Label.Small.SemiBold>
                                <ScoreTag ratingScore={memberReviews} />

                                <Label.XSmall.SemiBold.N50>
                                    {singularPlural(
                                        `${memberReviews.ratingsCount} review`,
                                        `${memberReviews.ratingsCount} reviews`,
                                        memberReviews.ratingsCount,
                                    )}
                                </Label.XSmall.SemiBold.N50>
                            </>
                        ) : (
                            <Label.XSmall.SemiBold.N60>
                                No Albertine member reviews yet
                            </Label.XSmall.SemiBold.N60>
                        )}
                    </Lib.Flex.Row>
                )}

                {googleReview && (
                    <Lib.Flex.Row gap="value8">
                        <Icon.StarRate.Medium.N10 />
                        <GoogleReview label="Google" reviews={googleReview} />
                    </Lib.Flex.Row>
                )}

                {openingHours && (
                    <Lib.Flex.Row gap="value8">
                        <Icon.Schedule.Medium.N10 />
                        <OpeningHours
                            googleUrl={googlePlaceUrl}
                            openingHours={openingHours}
                        />
                    </Lib.Flex.Row>
                )}

                {businessStatus && businessStatus !== "OPERATIONAL" && (
                    <Lib.Flex.Row gap="value8">
                        <Icon.QuestionMark.Medium.N10 />
                        <BusinessStatus businessStatus={businessStatus} />
                    </Lib.Flex.Row>
                )}
            </Lib.Flex.Column>
        </Lib.Flex.Row>
    );
}

export function CatalogItemDetailsPrimary({
    item,
    googlePlaceService,
}: {
    item: CatalogItemFull;
    googlePlaceService: google.maps.places.PlacesService;
}) {
    const { data: suppliers } = useSuppliers();
    const { data: placeDetails } = useCatalogItemPlaceDetails(item, googlePlaceService);
    const { data: reviewsData } = useGet("catalogItemReviews", {
        param: { id: item.lmtId },
    });
    const reviews =
        reviewsData && reviewsData.ratingsCount > 0 && reviewsData.average != null
            ? {
                  ratingsCount: reviewsData.ratingsCount,
                  average: reviewsData.average,
              }
            : undefined;

    const {
        address,
        addressUrl,
        googlePlaceUrl,
        offeringCategories,
        website,
        phoneNumber,
    } = parseViewDetails(item, suppliers ?? []);

    return (
        <PlaceDetailsPrimary
            offeringCategories={offeringCategories}
            website={website}
            phoneNumber={phoneNumber}
            address={address}
            addressUrl={addressUrl}
            googlePlaceUrl={googlePlaceUrl}
            showAddress={showAddress(item.offeringCategories)}
            googleReview={placeDetails ? placeDetails.reviews : undefined}
            openingHours={placeDetails?.openingHours}
            businessStatus={placeDetails?.businessStatus}
            memberReviews={reviews ?? null}
            className={classNames(item.archived && "catalog-item--archived")}
        />
    );
}
